import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaEnvelope, FaEnvelopeOpenText, FaFileMedicalAlt } from "react-icons/fa";
import { supabase } from "../supabaseClient";

function formatDateTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function PatientInbox({ session }) {
  const [messages, setMessages] = useState([]);
  const [discharges, setDischarges] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!session?.user?.id) return;
    setLoading(true);
    setError("");

    const [messageResult, dischargeResult] = await Promise.all([
      supabase
        .from("patient_messages")
        .select("id, subject, body, category, action_url, created_at, read_at")
        .eq("portal_account_id", session.user.id)
        .order("created_at", { ascending: false })
        .limit(50),
      supabase
        .from("discharge_summaries")
        .select("id, diagnosis, treatment, discharge_advice, follow_up, medications, discharged_at")
        .eq("portal_account_id", session.user.id)
        .order("discharged_at", { ascending: false })
        .limit(10),
    ]);

    if (messageResult.error || dischargeResult.error) {
      const loadError = messageResult.error || dischargeResult.error;
      console.error("Unable to load patient inbox:", loadError);
      setError("Your inbox could not be loaded. The clinical workflow upgrade may not have been installed yet.");
    }

    setMessages(messageResult.data || []);
    setDischarges(dischargeResult.data || []);
    setLoading(false);
  }, [session?.user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const unreadCount = useMemo(
    () => messages.filter((item) => !item.read_at).length,
    [messages],
  );

  async function openMessage(message) {
    setExpandedId((current) => (current === message.id ? null : message.id));
    if (message.read_at) return;

    const readAt = new Date().toISOString();
    const { error: updateError } = await supabase
      .from("patient_messages")
      .update({ read_at: readAt })
      .eq("id", message.id)
      .eq("portal_account_id", session.user.id);

    if (!updateError) {
      setMessages((current) =>
        current.map((item) =>
          item.id === message.id ? { ...item, read_at: readAt } : item,
        ),
      );
    }
  }

  return (
    <section id="patient-inbox" className="pguh-public-content-card pguh-patient-inbox">
      <div className="pguh-public-section-card__header">
        <div>
          <span className="pguh-public-kicker">Messages & documents</span>
          <h2>Patient inbox</h2>
        </div>
        <strong className="pguh-patient-inbox__count">{unreadCount} unread</strong>
      </div>

      {loading ? (
        <p>Loading your messages…</p>
      ) : error ? (
        <div className="pguh-public-error" role="alert">{error}</div>
      ) : (
        <>
          <div className="pguh-patient-inbox__list">
            {messages.length === 0 ? (
              <div className="pguh-public-empty">
                <FaEnvelope aria-hidden="true" />
                <p>You do not have any messages yet.</p>
              </div>
            ) : (
              messages.map((item) => {
                const expanded = expandedId === item.id;
                return (
                  <article key={item.id} className={`pguh-patient-inbox-message${item.read_at ? "" : " is-unread"}`}>
                    <button type="button" onClick={() => openMessage(item)} aria-expanded={expanded}>
                      <span className="pguh-patient-inbox-message__icon">
                        {item.read_at ? <FaEnvelopeOpenText /> : <FaEnvelope />}
                      </span>
                      <span className="pguh-patient-inbox-message__summary">
                        <small>{item.category || "General"} · {formatDateTime(item.created_at)}</small>
                        <strong>{item.subject}</strong>
                      </span>
                      {!item.read_at && <span className="pguh-patient-inbox-message__unread">New</span>}
                    </button>
                    {expanded && (
                      <div className="pguh-patient-inbox-message__body">
                        <p>{item.body}</p>
                      </div>
                    )}
                  </article>
                );
              })
            )}
          </div>

          {discharges.length > 0 && (
            <div className="pguh-patient-discharge-documents">
              <h3 className="nhsuk-heading-s"><FaFileMedicalAlt aria-hidden="true" /> Visit summaries</h3>
              {discharges.map((summary) => (
                <details key={summary.id}>
                  <summary>
                    <strong>{summary.diagnosis}</strong>
                    <span>{formatDateTime(summary.discharged_at)}</span>
                  </summary>
                  <div>
                    {summary.treatment && <p><strong>Treatment:</strong> {summary.treatment}</p>}
                    {summary.medications && <p><strong>Medication:</strong> {summary.medications}</p>}
                    {summary.discharge_advice && <p><strong>Advice:</strong> {summary.discharge_advice}</p>}
                    {summary.follow_up && <p><strong>Follow-up:</strong> {summary.follow_up}</p>}
                  </div>
                </details>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
